import React, { useState, useEffect } from 'react';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { CheckCircle, User, Briefcase, DollarSign, Ruler, FileText, Globe, Star, Shield, Home } from "lucide-react";
import {
  HomeIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon
} from '@heroicons/react/24/solid';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

import axios from 'axios';

const MortgageLoan = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [activeTab, setActiveTab] = useState('common');
  const [activeFAQ, setActiveFAQ] = useState(0);  // OPEN FIRST FAQ BY DEFAULT
  const [activeCalc, setActiveCalc] = useState("overview");
  const [bannerData, setBannerData] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [benefitsData, setBenefitsData] = useState([]);
  const [benefitsSettings, setBenefitsSettings] = useState(null);
  const [eligibilityData, setEligibilityData] = useState([]);
  const [eligibilitySettings, setEligibilitySettings] = useState(null);
  const [documentsData, setDocumentsData] = useState({});
  const [documentsSettings, setDocumentsSettings] = useState(null);
  const [interestRatesData, setInterestRatesData] = useState([]);
  const [interestRatesSettings, setInterestRatesSettings] = useState(null);
  const [faqsData, setFaqsData] = useState([]);
  const [faqsSettings, setFaqsSettings] = useState(null);
  const [ctaBannerSettings, setCtaBannerSettings] = useState(null);

  // Fallback banner data
  const fallbackBanner = {
    image_url: 'https://via.placeholder.com/1920x600?text=Mortgage+Loan+Banner',
    title: 'Your Dream Home Awaits',
    description: 'Secure your future with our flexible mortgage loan options.',
    button_text: 'Apply Now',
    button_link: '/apply-mortgage-loan'
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };

  // Fetch banner data
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        console.log('Fetching banner for mortgage_loan...');
        const response = await axios.get('/api/admin/banners/page/mortgage_loan');
        console.log('Banner response:', response.data);

        const banners = response.data.data || [];

        if (response.data.success && banners.length > 0) {
          console.log('Setting banner data:', banners[0]);
          setBannerData(banners[0]);
        } else {
          console.log('No banner data found, using fallback');
          setBannerData(fallbackBanner);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        setBannerData(fallbackBanner);
      } finally {
        setBannerLoading(false);
      }
    };

    fetchBanner();
  }, []);

  // Fetch Overview Data
  useEffect(() => {
    const fetchOverviewData = async () => {
      try {
        const response = await axios.get('/api/admin/loan-overview/mortgage_loan');
        if (response.data.success && response.data.data) {
          setOverviewData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching overview data:', error);
      }
    };

    fetchOverviewData();
  }, []);

  // Fetch Key Benefits Data
  useEffect(() => {
    const fetchBenefitsData = async () => {
      try {
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/mortgage_loan/active'),
          axios.get('/api/admin/key-benefits/settings/mortgage_loan')
        ]);

        if (benefitsRes.data.data) {
          setBenefitsData(benefitsRes.data.data);
        }
        if (settingsRes.data.data) {
          setBenefitsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching key benefits data:', error);
      }
    };

    fetchBenefitsData();
  }, []);

  // Fetch Eligibility Criteria Data
  useEffect(() => {
    const fetchEligibilityData = async () => {
      try {
        const [criteriaRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/eligibility/mortgage_loan/active'),
          axios.get('/api/admin/eligibility/settings/mortgage_loan')
        ]);

        if (criteriaRes.data.data) {
          setEligibilityData(criteriaRes.data.data);
        }
        if (settingsRes.data.data) {
          setEligibilitySettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching eligibility data:', error);
      }
    };

    fetchEligibilityData();
  }, []);

  // Fetch Documents Data
  useEffect(() => {
    const fetchDocumentsData = async () => {
      try {
        const [documentsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/documents/mortgage_loan/active'),
          axios.get('/api/admin/documents/settings/mortgage_loan')
        ]);

        if (documentsRes.data.data) {
          // Group documents by category
          const grouped = {};
          documentsRes.data.data.forEach(doc => {
            if (!grouped[doc.category]) {
              grouped[doc.category] = [];
            }
            grouped[doc.category].push(doc.document_name);
          });
          setDocumentsData(grouped);
        }
        if (settingsRes.data.data) {
          setDocumentsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching documents data:', error);
      }
    };

    fetchDocumentsData();
  }, []);

  // Fetch Interest Rates Data
  useEffect(() => {
    const fetchInterestRatesData = async () => {
      try {
        const [cardsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/interest-rates/mortgage_loan/active'),
          axios.get('/api/admin/interest-rates/settings/mortgage_loan')
        ]);

        if (cardsRes.data.data) {
          setInterestRatesData(cardsRes.data.data);
        }
        if (settingsRes.data.data) {
          setInterestRatesSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching interest rates data:', error);
      }
    };

    fetchInterestRatesData();
  }, []);

  // Fetch FAQs Data
  useEffect(() => {
    const fetchFaqsData = async () => {
      try {
        const [faqsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/faqs/mortgage_loan/active'),
          axios.get('/api/admin/faqs/settings/mortgage_loan')
        ]);

        if (faqsRes.data.data) {
          setFaqsData(faqsRes.data.data);
        }
        if (settingsRes.data.data) {
          setFaqsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching FAQs data:', error);
      }
    };

    fetchFaqsData();
  }, []);

  // Fetch CTA Banner Data
  useEffect(() => {
    const fetchCtaBannerData = async () => {
      try {
        const response = await axios.get('/api/admin/cta-banner/settings/mortgage_loan');
        if (response.data.data) {
          setCtaBannerSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA banner data:', error);
      }
    };

    fetchCtaBannerData();
  }, []);

  // Key Benefits Data
  const benefits = [
    {
      icon: BanknotesIcon,
      title: 'Loan up to ₹30 Lakhs',
      description: 'Get substantial funding for your dream home construction'
    },
    {
      icon: ClockIcon,
      title: 'Tenure up to 20 Years',
      description: 'Flexible repayment options that suit your financial planning'
    },
    {
      icon: ChartBarIcon,
      title: 'Disbursement Linked to Progress',
      description: 'Funds released based on construction milestones'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Competitive Interest Rates',
      description: 'Enjoy attractive rates designed for your benefit'
    },
    {
      icon: CurrencyRupeeIcon,
      title: 'No Prepayment Penalty',
      description: 'Pay off your loan early without any additional charges'
    },
    {
      icon: HomeIcon,
      title: 'Covers Renovation & Repair',
      description: 'Finance both new construction and home improvements'
    }
  ];

  // Eligibility Criteria
  const eligibility = [
    'Age: 21 to 65 years',
    'Indian Residents or NRIs',
    'Salaried, Self-Employed, or Business Professionals',
    'Stable income source',
    'Approved construction plan and building permit',
    'Clear land title',
    'Good credit history preferred but not mandatory',
    "Minimum plot size requirement",
  ];
  // Matching icons & colors for each item
  const icons = [
    { icon: User, from: "blue-500", to: "cyan-400" },
    { icon: Globe, from: "green-500", to: "lime-400" },
    { icon: Briefcase, from: "pink-500", to: "rose-400" },
    { icon: DollarSign, from: "orange-500", to: "amber-400" },
    { icon: FileText, from: "purple-500", to: "indigo-400" },
    { icon: Shield, from: "teal-500", to: "emerald-400" },
    { icon: Star, from: "yellow-500", to: "orange-400" },
    { icon: Ruler, from: "red-500", to: "orange-400" },
  ];

  // Documents Required
  const documents = {
    common: [
      'Identity Proof (Aadhaar, PAN, Passport, Voter ID)',
      'Address Proof (Utility Bills, Aadhaar)',
      'Recent Passport-size Photographs',
      'Bank Statements (Last 6 months)'
    ],
    salaried: [
      'Last 3 Months Salary Slips',
      'Form 16 / IT Returns (Last 2 Years)',
      'Employment Letter / Appointment Letter',
      'Employee ID Card'
    ],
    selfEmployed: [
      'Business Registration Documents',
      'IT Returns / Audited Financials (Last 2-3 Years)',
      'GST Returns (if applicable)',
      'Business Address Proof',
      'Bank Statements of Business Account',
      "Business License / Trade License"
    ],
    nri: [
      'Valid Passport & Visa',
      'Overseas Address Proof',
      'NRE/NRO Bank Statements',
      'Employment Contract / Proof of Income Abroad',
      'Power of Attorney (if applicable)',
      "OCI/PIO Card (if applicable)"
    ],
    property: [
      'Land Purchase Agreement / Sale Deed',
      'Title Deed & Encumbrance Certificate',
      'Approved Building Plan from Local Authority',
      'NOC from Society / Local Authority',
      'Construction Estimate / Cost Sheet',
      'Property Tax Receipts',
      'Land Survey Documents',
      "Soil Test Report (if applicable)"
    ]
  };

  // FAQs
  const faqs = [
    {
      question: 'What is a Home Construction Loan?',
      answer: 'A Home Construction Loan helps you build a new house on a plot of land you already own. Aham Housing Finance provides funds in stages as construction progresses, ensuring financial support throughout your building journey.'
    },
    {
      question: 'Do I need income proof to apply?',
      answer: 'At Aham, we understand that not everyone has traditional income documents. While having ITR or salary slips helps expedite approval, we also consider alternative income verification methods to support your loan application.'
    },
    {
      question: 'How is the loan amount disbursed?',
      answer: 'The loan is disbursed in stages based on construction progress. Typically: 20% on completion of foundation, 35% on completion of lintel level, 25% on completion of roof, and 20% on final completion. Our team conducts site inspections before each disbursement.'
    },
    {
      question: 'What is the maximum loan tenure?',
      answer: 'Aham offers flexible repayment tenures of up to 20 years, allowing you to choose a repayment schedule that aligns with your financial capabilities and future plans.'
    },
    {
      question: 'Can I prepay my loan without penalty?',
      answer: 'Yes! Aham Housing Finance does not charge any prepayment penalty. You can pay off your loan partially or fully at any time without incurring additional costs, helping you save on interest.'
    },
    {
      question: 'What if I already own land?',
      answer: 'Perfect! Our Home Construction Loan is designed for individuals who own a plot or land and wish to construct a residential property on it. Just ensure you have clear land title and approved building plans.'
    },
    {
      question: 'Are NRIs eligible for this loan?',
      answer: 'Yes, NRIs are eligible to apply for Home Construction Loans from Aham. You will need to provide documents such as passport, visa, overseas address proof, and NRE/NRO bank statements along with standard property documents.'
    },
    {
      question: 'How long does the approval process take?',
      answer: 'Aham strives for quick and efficient processing. Once all documents are submitted, loan approval typically takes 7-10 working days. Our dedicated team works to minimize delays and keep you informed throughout the process.'
    },
    {
      question: 'Can I use the loan for renovation or extension?',
      answer: 'Yes! In addition to new construction, Aham Home Construction Loans can also be used for significant home renovations, extensions, or repairs to your existing property, giving you flexibility in how you use the funds.'
    }
  ];

  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };


  // Scroll Event Listener to auto-switch active tab
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "benefits", "eligibility", "documents", "interest", "faq"];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveCalc(sec);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerHeight = 120; // top navbar + sticky navbar height

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  // Icon mapping function for dynamic benefits
  const getIconComponent = (iconName) => {
    const iconMap = {
      'BanknotesIcon': BanknotesIcon,
      'ClockIcon': ClockIcon,
      'ChartBarIcon': ChartBarIcon,
      'ShieldCheckIcon': ShieldCheckIcon,
      'CurrencyRupeeIcon': CurrencyRupeeIcon,
      'HomeIcon': HomeIcon,
      'DocumentTextIcon': DocumentTextIcon
    };
    return iconMap[iconName] || BanknotesIcon; // Default to BanknotesIcon if not found
  };

  // Default banner data if API fails or no data
  const defaultBanner = {
    image_url: 'img/loan-banners/Mortgage-Loan-Banner.webp',
    title: 'Unlock Your Property’s Value with Aham Mortgage Loan',
    subtitle: 'Get high-value funding for business or personal needs by pledging your residential property at competitive rates.',
    primary_button_text: 'Apply Now',
    primary_button_link: 'https://aham.mbwhost.in/contact',
    secondary_button_text: 'EMI Calculator',
    secondary_button_link: 'https://aham.mbwhost.in/emi-calculator'
  };

  const banner = bannerData || defaultBanner;
  const imageUrl = banner.image_url?.startsWith('http')
    ? banner.image_url
    : `/${banner.image_url?.startsWith('/') ? banner.image_url.slice(1) : banner.image_url}`;
            
            
            // Schema for SEO
  const mortgageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
         "areaServed": {
          "@type": "Country",
          "name": "India"
        },
         "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/loan-against-property-mortgage-loan/#webpage",
        "url": "https://ahamhfc.com/loan-against-property-mortgage-loan",
        "name": "Loan Against Property / Mortgage Loan - Aham Housing Finance",
        "description": "Apply for a mortgage loan from Aham Housing Finance at attractive interest rates and unlock up to 75% of your property value with easy EMIs and fast approval.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/loan-against-property-mortgage-loan/#loan"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/loan-against-property-mortgage-loan/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/Mortgage-loan-1768817588488-263701541.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "LoanOrCredit",
        "@id": "https://ahamhfc.com/loan-against-property-mortgage-loan/#loan",
        "name": "Mortgage Loan",
        "alternateName": "Loan Against Property",
        "url": "https://ahamhfc.com/loan-against-property-mortgage-loan",
        "image": {
          "@id": "https://ahamhfc.com/loan-against-property-mortgage-loan/#primaryimage"
        },
        "description": "Loan against property offering attractive interest rates, easy EMIs, fast approval, and funding up to 75% of property value.",
        "category": "Housing Finance",
        "loanType": "Loan Against Property",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "amount": {
          "@type": "MonetaryAmount",
          "currency": "INR"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Property owners seeking secured finance for financial needs"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/loan-against-property-mortgage-loan/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Mortgage Loan",
            "item": "https://ahamhfc.com/loan-against-property-mortgage-loan"
          }
        ]
      }
    ]
  };


  return (
    <div className="min-h-screen bg-white">

      <SEO
        title="Loan Against Property: Attractive Rates | Aham Housing Finance"
        description="Get a Loan Against Property from Aham Housing Finance with attractive rates, flexible EMIs, and quick approvals. Apply today!"
        keywords="Aham mortgage loan, Aham Housing Finance mortgage loan, mortgage loan, loan against property, property mortgage loan, mortgage finance India, loan for house construction on own land, self construction mortgage loan, mortgage loan on owned land, affordable mortgage loan, low documentation mortgage loan, no income proof mortgage loan, self employed mortgage loan, quick approval mortgage loan, housing finance mortgage loan, property backed loan, secured loan against property, Aham loan against property, Aham property mortgage finance, Aham Housing Finance loan against property"
        canonical="https://ahamhfc.com/loan-against-property-mortgage-loan"
        ogTitle="Loan Against Property: Attractive Rates | Aham Housing Finance"
        ogDescription="Apply for a mortgage loan at attractive interest rates. Unlock up to 75% of your property value with easy EMIs & fast approval. No income proof? Apply with Aham now!"
       schema={mortgageSchema}
           />

      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {bannerLoading ? (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${imageUrl})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
             
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
                  {banner.title}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner.subtitle}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  {banner.primary_button_text && (
                    <a href={banner.primary_button_link || "https://aham.mbwhost.in/contact"} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0"
                      >
                        {banner.primary_button_text}
                      </motion.button>
                    </a>
                  )}

                  {banner.secondary_button_text && (
                    <a href={banner.secondary_button_link || "#"} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0 border border-white/20"
                      >
                        {banner.secondary_button_text}
                      </motion.button>
                    </a>
                  )}
                </div>
                 {/* Developer Ilaiyaboopathi R */}
                
                   {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Mortgage Loan</span>
                </motion.div>

                
              </motion.div>
            </div>
          </>
        )}
      </section>


      {/* Sticky Finance Navigation */}
      <div className="hidden md:block w-full py-3 sticky top-[80px] z-50">
        <div className="container mx-auto px-1">

          <div className="w-full bg-white rounded-full shadow-lg flex flex-wrap justify-center gap-2 md:gap-6 py-3 px-4">

            {[
              { id: "overview", label: "Overview" },
              { id: "benefits", label: "Benefits" },
              { id: "eligibility", label: "Loan Eligibility Criteria" },
              { id: "documents", label: "Documents" },
              { id: "interest", label: "Interest" },
              { id: "faq", label: "FAQ" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveCalc(tab.id);
                  scrollToSection(tab.id); // ONLY this — smooth scroll with offset
                }}
                className={`
    px-6 py-2 rounded-full font-semibold transition-all 
    ${activeCalc === tab.id
                    ? "bg-yellow-400 text-blue-800 shadow-lg scale-105"
                    : "text-gray-500 hover:text-blue-700"
                  }
  `}
              >
                {tab.label}
              </button>

            ))}
          </div>
        </div>
      </div>

           {/* overview section */}
     <section id="overview" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center md:text-left">
                {overviewData?.title || 'Mortgage Loan'}
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                {overviewData?.description ? (
                  overviewData.description.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))
                ) : (
                  <>
                    <p>
                      Building your dream home is a significant milestone, and Aham Housing Finance is here to make it easier. Our <strong>Mortgage Loan</strong> is designed to provide financial assistance for constructing a new house on land you already own.
                    </p>
                    <p>
                      Whether you're planning a simple structure or a luxury villa, we offer flexible loan options tailored to your construction needs.
                    </p>
                  </>
                )}
                <ul className="space-y-3">
                  {overviewData?.features ? (() => {
                    try {
                      const features = typeof overviewData.features === 'string'
                        ? JSON.parse(overviewData.features)
                        : overviewData.features;
                      return features.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                          <span>{feature}</span>
                        </li>
                      ));
                    } catch (error) {
                      console.error('Error parsing features:', error);
                      return null;
                    }
                  })() : (
                    <>
                      <li className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                        <span><strong>No strict income proof requirements</strong> – We understand your unique financial situation</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                        <span><strong>Flexible disbursement</strong> – Funds released in stages as construction progresses</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                        <span><strong>Competitive interest rates</strong> – Affordable rates designed to suit your budget</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                        <span><strong>Quick approval process</strong> – Fast-track processing to get you started sooner</span>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={overviewData?.image_url || 'img/loan-overview/Mortgage-loan.webp'}
                alt="Aham Housing Finance expert discussing mortgage loan benefits and attractive interest rates with a happy business owner"
                className="rounded-2xl shadow-large w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>





      {/* Key Benefits Section */}
      <section id="benefits" className="relative py-24 overflow-hidden">

        {/* 🔥 Background – gradient + subtle pattern */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-1"
          style={{
            backgroundImage:
              "url('/img/construction-loan/Key-Benefits.webp')",
          }}
        ></div>

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/100 via-white/70 to-white"></div>

        <div className="relative container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-white drop-shadow-lg mb-6 leading-tight">
              {benefitsSettings?.section_title || 'Key Benefits & Features'}
            </h2>
            <p className="text-lg md:text-xl text-white mt-4 max-w-3xl mx-auto leading-relaxed font-medium">
              {benefitsSettings?.section_subtitle || 'Discover why Aham Mortgage Loan is the right choice for your financial needs'}
            </p>
          </motion.div>

          {/* Benefit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {(benefitsData.length > 0 ? benefitsData : benefits).map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -12, scale: 1.03 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="relative bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center"
              >
                {/* Floating Glow */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary-500 rounded-full blur-3xl opacity-20"></div>

                {/* Icon */}
                <div className="bg-gradient-to-br from-primary-600 to-primary-400 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  {benefit.icon ? (
                    <benefit.icon className="h-9 w-9 text-white" />
                  ) : (
                    React.createElement(getIconComponent(benefit.icon_name), { className: "h-9 w-9 text-white" })
                  )}
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>

                {/* Description */}
                <p className="text-gray-700 leading-relaxed font-medium">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 flex justify-center">
            <a href="/contact" className="w-full flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                Request a Quote
              </motion.button>
            </a>
          </div>
        </div>
      </section>

      {/* Eligibility Criteria Section */}
      <section id="eligibility" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Soft Background Blobs */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 blur-3xl rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">

          <div className="max-w-4xl mx-auto text-center mb-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 bg-primary-100 text-primary-700 rounded-full font-medium shadow-sm mb-6"
            >
              Loan Eligibility
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-6 mb-4 leading-tight tracking-tight"
            >
              {eligibilitySettings?.section_title || 'Who Can Apply?'}
            </motion.h2>

            <p className="text-lg md:text-xl text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              {eligibilitySettings?.section_subtitle || "Check your eligibility for an Aham mortgage loan"}
            </p>
          </div>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(eligibilityData.length > 0 ? eligibilityData : eligibility).map((item, index) => {
              // Icon mapping for dynamic data
              const iconMap = {
                'UserIcon': User,
                'GlobeAltIcon': Globe,
                'BriefcaseIcon': Briefcase,
                'CurrencyRupeeIcon': DollarSign,
                'DocumentTextIcon': FileText,
                'ShieldCheckIcon': Shield,
                'StarIcon': Star,
                'ScaleIcon': Ruler
              };

              const IconComponent = item.icon_name ? (iconMap[item.icon_name] || User) : icons[index]?.icon || User;
              const gradientFrom = item.gradient_from || icons[index]?.from || 'blue-500';
              const gradientTo = item.gradient_to || icons[index]?.to || 'cyan-400';

              return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="
                group relative backdrop-blur-xl bg-white/70 
                border border-white/40 shadow-xl rounded-2xl 
                p-6 flex flex-col items-center text-center gap-5 
                hover:-translate-y-2 hover:shadow-2xl 
                transition-all duration-300
              "
                  >
                    {/* Icon with Glowing Gradient Background */}
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-${gradientFrom} to-${gradientTo} flex items-center justify-center mb-6 shadow-lg group-hover:rotate-6 transition-transform duration-300`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>

                    {/* Criteria Text */}
                    <h3 className="text-lg font-bold text-gray-900 leading-snug">
                      {item.criteria || item}
                    </h3>
                  </motion.div>
              );
            })}
          </div>

          {/* CTA Button */}
          {/* <div className="text-center mt-16">
            <a href="/customer-360-eligibility-calculator">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="
              px-10 py-4 rounded-full text-white font-semibold 
             bg-[#ed2636]
              shadow-lg hover:bg-primary-600 transition
            "
              >
                Check Your Eligibility Now
              </motion.button>
            </a>
          </div> */}

        </div>
      </section>

      {/* Documents Required Section */}
      <section id="documents" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Background blur circles */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4">
              {documentsSettings?.section_title || 'Documents Required'}
            </h2>
            <p className="text-xl text-gray-600">
              {documentsSettings?.section_subtitle || 'Keep these documents ready for a smooth application process'}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">

            {/* Premium Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { key: "common", label: "Common", icon: <FileText /> },
                { key: "salaried", label: "Salaried", icon: <Briefcase /> },
                { key: "selfEmployed", label: "Self-Employed", icon: <User /> },
                { key: "nri", label: "NRI", icon: <Globe /> },
                { key: "property", label: "Property", icon: <Home /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 backdrop-blur-xl
              border border-white/40 shadow-lg
              ${activeTab === tab.key
                      ? "bg-gradient-to-r from-primary-600 to-primary-500 text-white scale-105 shadow-primary/50"
                      : "bg-white/70 text-gray-700 hover:bg-white/90"
                    }
            `}
                >
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Card */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="backdrop-blur-xl bg-white/70 p-10 rounded-3xl border border-white/40 shadow-xl"
            >
              <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(Object.keys(documentsData).length > 0 ? documentsData[activeTab] || [] : documents[activeTab] || []).map((doc, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className="
                flex items-start gap-4 
                p-5 bg-gradient-to-br from-white to-gray-50 
                rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200
              "
                  >
                    <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center shadow-inner">
                      <FileText className="h-6 w-6" />
                    </div>
                    <span className="text-gray-900 font-semibold">{doc}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

          </div>
        </div>
      </section>


      {/* Interest Rates Section */}
      <section
        id="interest"
        className="relative py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white overflow-hidden"
      >
        {/* Decorative Blurs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl opacity-30"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Title */}
            <h2 className="text-5xl md:text-6xl font-extrabold mb-8 leading-tight drop-shadow-xl">
              {interestRatesSettings?.section_title || 'Affordable Home Loan'}
              <span className="block text-primary-200 mt-2">{interestRatesSettings?.section_subtitle || 'Interest Rates'}</span>
            </h2>

            {/* Description Section */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-10 md:p-14 shadow-2xl">
              <p className="text-xl text-white/90 leading-relaxed mb-6">
                {interestRatesSettings?.intro_text || 'Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make home construction affordable and stress-free.'}
              </p>

              <div className="grid md:grid-cols-3 gap-8 my-10">
                {(interestRatesData.length > 0 ? interestRatesData : [
                  { icon: '🏠', title: 'Flexible Rates', description: 'Rates tailored based on loan amount, tenure, credit profile & location.' },
                  { icon: '🔍', title: '100% Transparency', description: 'No hidden fees. No surprise charges. Complete clarity from start to finish.' },
                  { icon: '🤝', title: 'Dedicated Support', description: 'Our expert team guides you at every stage of your construction journey.' }
                ]).map((card, index) => (
                   <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 + (index * 0.2) }}
                    className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg flex flex-col items-center text-center"
                  >
                    <div className="text-3xl mb-3">{card.icon}</div>
                    <h4 className="text-xl font-semibold mb-2">{card.title}</h4>
                    <p className="text-white/80 text-sm leading-relaxed font-medium">
                      {card.description}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Final Message */}
              <p className="text-lg text-white/85 mb-10 leading-relaxed">
                {interestRatesSettings?.closing_text || 'Begin your home construction journey with confidence and clarity. Aham ensures the process is smooth, transparent, and tailored to your needs.'}
              </p>

              {/* CTA Button */}
              {/* <motion.a
                href={interestRatesSettings?.cta_link || 'https://aham.mbwhost.in/emi-calculator'}
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white-700 hover:text-black hover:bg-primary-50 px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 shadow-xl"
              >
                {interestRatesSettings?.cta_text || 'Calculate EMI'}
              </motion.a> */}
            </div>
          </motion.div>
        </div>
      </section>


      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {faqsSettings?.section_title || 'Frequently Asked Questions'}
            </h2>
            <p className="text-xl text-gray-600">
              {faqsSettings?.section_subtitle || 'Find answers to common questions about Home Construction Loan'}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {(faqsData.length > 0 ? faqsData : faqs).map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="mb-4"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className={`w-full text-left p-6 rounded-xl transition-all duration-300 ${activeFAQ === index
                    ? 'bg-primary-50 border-2 border-primary-600'
                    : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {activeFAQ === index ? (
                      <ChevronUpIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  {activeFAQ === index && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 text-gray-700 leading-relaxed"
                    >
                      {faq.answer}
                    </motion.p>
                  )}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(img/construction-loan/home-construction-loan.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaBannerSettings?.title || 'Ready to Start Building Your Dream Home?'}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
              {ctaBannerSettings?.description || 'Take the first step towards constructing your perfect home with Aham\'s hassle-free loan process'}
            </p>
             <div className="flex justify-center">
              <motion.a
                href={ctaBannerSettings?.button_link || 'https://aham.mbwhost.in/contact'}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:bg-white hover:text-primary-600 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto"
              >
                {ctaBannerSettings?.button_text || 'Talk to Our Loan Expert'}
              </motion.a>
            </div>

          </motion.div>
        </div>
      </section>


    </div>
  );
};

export default MortgageLoan;